import React, { useMemo } from 'react';
import I18n from '@/modules/i18n';
import {
  shared_program_dashboard_index_path,
  shared_program_lesson_path,
  shared_program_unit_path,
  teacher_score_path,
} from '@/modules/routes';
import Breadcrumbs, { BreadcrumbItem } from './Breadcrumbs';

export interface BreadcrumbsNavigatorProps {
  largerFont?: boolean;
  lesson?: { id: number; title?: string };
  page: 'grades';
  pageTitle: string;
  programId?: number;
  searchParams?: URLSearchParams;
  unit?: { id: number; title?: string };
}

// This is a React Component that was converted from code in breadcrumbs_helper.rb
const BreadcrumbsNavigator = ({
  largerFont = false,
  lesson,
  page,
  pageTitle,
  programId,
  searchParams,
  unit,
}: BreadcrumbsNavigatorProps) => {
  const classroomId = searchParams.get('classroom_id');
  const lessonId = searchParams.get('lesson_id');
  const unitId = searchParams.get('unit_id');

  const links = useMemo(() => {
    switch (page) {
    case 'grades':
      return ['home', (unitId ? 'unit' : 'lesson'), 'grades'];
    default:
      return ['home', 'unit', 'lesson'];
    }
  }, [page, lessonId, unitId]);

  const gradesPathParams = () => {
    const unitOrLessonId = lessonId ? { lesson_id: lessonId } : { unit_id: unitId };

    return {
      program_id: programId,
      ...unitOrLessonId,
    };
  };

  const generateBreadcrumbItem = (label: string): BreadcrumbItem => {
    switch (label) {
    case 'home':
      return {
        href: programId ? shared_program_dashboard_index_path(programId) : '/',
        label: I18n.t('home'),
      };

    case 'unit':
      return {
        href: shared_program_unit_path(programId, unitId || unit?.id),
        label: unit?.title || I18n.t('unit'),
      };

    case 'lesson':
      return {
        href: shared_program_lesson_path(programId, lessonId || lesson?.id),
        label: lesson?.title || I18n.t('lesson'),
      };

    case 'grades':
      return {
        href: classroomId ? teacher_score_path(classroomId, gradesPathParams()) : '',
        label: I18n.t('grades'),
      };

    default:
      return {
        href: '#',
        label: I18n.t(label) || label,
      };
    }
  };

  const items: BreadcrumbItem[] = useMemo(() => (
    links.map(label => generateBreadcrumbItem(label))
  ), [links, largerFont, programId, classroomId, lessonId, unitId]);

  return (
    <Breadcrumbs
      items={items.concat({ label: pageTitle })}
      largerFont={largerFont}
    />
  );
};

export default BreadcrumbsNavigator;
